import DOMPurify from 'dompurify';
import './EmailPreviewModal.css';
import { EmailSignatureSelector } from './EmailSignatureSelector';
import { useEmailSignature } from './useEmailSignature';
import { ReviewCountField } from './ReviewCountField';
import { useReviewCount } from './useReviewCount';
import { signatureInputError, hasSignatureBlock, getSignatureBlock } from './emailSignature';
import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';


const EmailPreviewModal = ({ 
  isOpen, 
  onClose, 
  modalData, 
  onSubjectEdit,
  onMessageEdit, 
  onSend,
  onTemplateChange,
  onAfterpayLinkChange,
  onSignatureUpdate
}) => {
  const changeSignature = useEmailSignature(isOpen, modalData, onSignatureUpdate);
  const reviewEnabled = isOpen && !modalData.loading && modalData.templateKey === 'email.review_request';
  const reviewCount = useReviewCount(reviewEnabled, `${modalData.petId}:${modalData.templateKey}`, modalData.renderedBody, onMessageEdit);
  const signatureEdited = hasSignatureBlock(modalData.renderedBody) && modalData.signatureHtml
    && getSignatureBlock(modalData.renderedBody) !== modalData.signatureHtml;
  const signatureError = modalData.signatureError || signatureInputError(modalData.signature)
    || (!hasSignatureBlock(modalData.renderedBody) ? 'The signature section is missing or duplicated. Restore it in the message before sending.' : '');
  const signatureBlocked = !!signatureError || !!signatureEdited || modalData.signatureLoading;
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" data-testid="email-preview-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold" data-testid="email-modal-title">📧 Send Email</h2>
              <p className="text-sm text-green-100 mt-1">
                {modalData.clientName} - {modalData.petName}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={modalData.sending || reviewCount.busy}
              className="text-white hover:text-gray-200 text-2xl font-bold"
              data-testid="email-modal-close"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-4">
          {modalData.loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading template...</p>
            </div>
          )}
          
          {modalData.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{modalData.error}</p>
              <button
                onClick={() => onSend()}
                disabled={signatureBlocked || reviewCount.blocked || modalData.loading || modalData.sending}
                data-testid="email-send-retry"
                className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Retry
              </button>
            </div>
          )}
          
          {modalData.success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-green-800 font-medium text-lg">Email Sent Successfully!</p>
              <p className="text-green-600 text-sm mt-1">The workflow step has been marked as completed.</p>
            </div>
          )}
          
          {!modalData.loading && !modalData.success && (
            <>
              {/* Template Selector - shown when multiple templates available */}
              {modalData.availableTemplates && modalData.availableTemplates.length > 1 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="email-template-selector">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Email Template ({modalData.availableTemplates.length} available)
                  </label>
                  <div className="space-y-2">
                    {modalData.availableTemplates.map(t => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => onTemplateChange && onTemplateChange(t.key)}
                        disabled={signatureBlocked || modalData.sending || reviewCount.busy}
                        data-testid={`email-template-option-${t.key}`}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all text-sm ${
                          modalData.templateKey === t.key
                            ? 'border-blue-500 bg-white shadow-sm ring-1 ring-blue-200'
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className={`font-medium ${modalData.templateKey === t.key ? 'text-blue-700' : 'text-gray-700'}`}>
                              {t.name}
                            </span>
                            {t.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
                            )}
                          </div>
                          {modalData.templateKey === t.key && (
                            <span className="text-blue-600 text-xs font-medium bg-blue-100 px-2 py-0.5 rounded">Selected</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  {modalData.previousPetInfo && (
                    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800" data-testid="previous-pet-info">
                      <span className="font-medium">Returning Customer:</span> Previous pet <strong>{modalData.previousPetInfo.pet_name}</strong> was allocated to <strong>{modalData.previousPetInfo.tree_planting_option === 'CN' ? 'Carbon Neutral' : modalData.previousPetInfo.tree_planting_option === 'OTP' ? 'One Tree Planted' : modalData.previousPetInfo.tree_planting_option}</strong>
                      {modalData.previousPetInfo.total_previous_pets > 1 && (
                        <span> ({modalData.previousPetInfo.total_previous_pets} previous pets found)</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Recipient Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">To:</span> {modalData.toEmail}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Client:</span> {modalData.clientName}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium">Pet:</span> {modalData.petName}
                </p>
              </div>
              
              {/* AfterPay URL input — only shown for the afterpay_link template */}
              {modalData.templateKey === 'email.other.afterpay_link' && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="afterpay-link-section">
                  <label className="block text-sm font-semibold text-amber-900 mb-1">
                    💳 AfterPay URL <span className="text-red-600">*</span>
                  </label>
                  <p className="text-xs text-amber-800 mb-2">
                    Paste the unique AfterPay payment link for this customer. It will be embedded in the email below.
                  </p>
                  <input
                    type="url"
                    value={modalData.afterpayLink || ''}
                    onChange={(e) => onAfterpayLinkChange && onAfterpayLinkChange(e.target.value)}
                    className="w-full px-3 py-2 border border-amber-400 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm font-mono"
                    placeholder="https://pay.afterpay.com/..."
                    data-testid="afterpay-link-input"
                    disabled={modalData.sending}
                  />
                  {!modalData.afterpayLink && (
                    <p className="text-xs text-red-600 mt-1">⚠️ Please paste the AfterPay link before sending</p>
                  )}
                  {modalData.afterpayError && <p role="alert" data-testid="afterpay-preview-error" className="text-sm text-red-700 mt-1">{modalData.afterpayError}</p>}
                </div>
              )}

              {/* Subject Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject Line (editable)
                </label>
                <input
                  type="text"
                  value={modalData.subject}
                  onChange={(e) => onSubjectEdit(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-sans text-sm"
                  placeholder="Email subject..."
                  data-testid="email-subject-input"
                  disabled={modalData.sending}
                />
              </div>
              
              {/* Message Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (editable HTML)
                </label>
                <textarea
                  value={modalData.renderedBody}
                  onChange={(e) => onMessageEdit(e.target.value)}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-xs"
                  placeholder="Email HTML content..."
                  data-testid="email-body-textarea"
                  disabled={modalData.sending}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {modalData.renderedBody.length} characters
                </p>
              </div>
              
              <EmailSignatureSelector
                value={modalData.signature} onChange={changeSignature}
                disabled={modalData.sending} loading={modalData.signatureLoading}
                error={signatureError}
                canApply={hasSignatureBlock(modalData.renderedBody) && !signatureInputError(modalData.signature) && (signatureEdited || !!modalData.signatureError)}
              />

              {/* Email Preview */}
              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-gray-700">Email Preview</span>
                  {reviewEnabled && <ReviewCountField state={reviewCount} disabled={modalData.sending} />}
                </div>
                <div className="border border-gray-300 rounded-lg p-4 bg-white max-h-96 overflow-y-auto" data-testid="email-preview-body">
                  <div className="email-html-preview prose prose-sm max-w-none text-gray-900" data-testid="email-preview-html" style={{color: '#333'}} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(modalData.renderedBody) }} />
                </div>
              </div>

              {/* Certificate PDF Preview — the exact PDFs that will be attached */}
              {modalData.isCertificateEmail && modalData.petId && (
                <div data-testid="certificate-pdf-preview-section">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📜 Certificate Preview — check pet name, dates & wording before sending
                  </label>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="border-2 border-teal-200 rounded-lg overflow-hidden bg-gray-50">
                      <div className="flex items-center justify-between px-3 py-2 bg-teal-50 border-b border-teal-200">
                        <span className="text-xs font-semibold text-teal-800">Cremation Certificate</span>
                        <a
                          href={`${API}/certificates/preview-pdf/${modalData.petId}?cert=aqua`}
                          target="_blank" rel="noreferrer"
                          className="text-xs text-teal-600 hover:text-teal-800 underline"
                          data-testid="cert-aqua-open-full"
                        >
                          Open full size ↗
                        </a>
                      </div>
                      <img
                        src={`${API}/certificates/preview-image/${modalData.petId}?cert=aqua`}
                        alt="Cremation Certificate Preview"
                        className="w-full bg-white"
                        data-testid="cert-aqua-image"
                      />
                    </div>
                    <div className="border-2 border-green-200 rounded-lg overflow-hidden bg-gray-50">
                      <div className="flex items-center justify-between px-3 py-2 bg-green-50 border-b border-green-200">
                        <span className="text-xs font-semibold text-green-800">{modalData.envCertType || 'Environmental'} Certificate</span>
                        <a
                          href={`${API}/certificates/preview-pdf/${modalData.petId}?cert=env`}
                          target="_blank" rel="noreferrer"
                          className="text-xs text-green-600 hover:text-green-800 underline"
                          data-testid="cert-env-open-full"
                        >
                          Open full size ↗
                        </a>
                      </div>
                      <img
                        src={`${API}/certificates/preview-image/${modalData.petId}?cert=env`}
                        alt="Environmental Certificate Preview"
                        className="w-full bg-white"
                        data-testid="cert-env-image"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    These are the exact PDFs that will be attached to the client's email.
                  </p>
                </div>
              )}
              
              {/* Send Button */}
              {(() => {
                const isAfterpay = modalData.templateKey === 'email.other.afterpay_link';
                const afterpayBlocking = isAfterpay && (!modalData.afterpayLink || modalData.afterpayError);
                const disabled = modalData.sending || afterpayBlocking || signatureBlocked || reviewCount.blocked;
                return (
              <div className="flex gap-3">
                <button
                  onClick={onSend}
                  disabled={disabled}
                  data-testid="email-send-button"
                  className={`flex-1 px-6 py-3 rounded-lg font-medium text-white
                    ${disabled 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700'
                    }`}
                >
                  {modalData.sending ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : afterpayBlocking ? (
                    'Paste AfterPay URL to enable send'
                  ) : (
                    'Send Email'
                  )}
                </button>
                <button
                  onClick={onClose}
                  disabled={modalData.sending || reviewCount.busy}
                  data-testid="email-preview-cancel"
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
                );
              })()}
              
              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <span className="font-medium">Note:</span> Once sent, the email will be delivered to the client's email address 
                  and the workflow step will be automatically updated.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// Add Appointment Modal Component

export default EmailPreviewModal;
